import * as tslib_1 from "tslib";
import React, { useMemo, useCallback } from 'react';
import _ from 'lodash';
import { withLocalize } from 'react-localize-redux';
import { useLocalize } from '../../../hooks';
import { Text } from '../Text';
import { SelectDropdownInput } from '../../fields';
// Custom filter function that prioritizes prefix matches and improves relevance
const customFilter = (option, inputValue) => {
    if (!inputValue)
        return true;
    const input = inputValue.toLowerCase().trim();
    // Helper function to safely convert to string
    const safeToString = (value) => {
        if (typeof value === 'string')
            return value;
        if (typeof value === 'number')
            return value.toString();
        if (React.isValidElement(value)) {
            // For JSX elements, try to extract text content or use a fallback
            return '';
        }
        return value ? String(value) : '';
    };
    const label = safeToString(option.label || '').toLowerCase();
    const value = safeToString(option.value || '').toLowerCase();
    const rawValue = safeToString(option.raw_value || '').toLowerCase();
    // Check all possible text fields for matches
    const searchableText = `${label} ${value} ${rawValue}`;
    // Exact prefix match (highest priority) - only match if input is at the start
    if (label.startsWith(input) || value.startsWith(input) || rawValue.startsWith(input)) {
        return true;
    }
    // Word boundary prefix match (high priority) - only match if input starts a word
    const words = searchableText.split(/\s+/);
    for (const word of words) {
        if (word.startsWith(input)) {
            return true;
        }
    }
    // Remove the general contains match - only allow prefix and word boundary matches
    // This prevents "Arizona" from matching when typing "n"
    return false;
};
const SelectDropdownFieldComponent = (_a) => {
    var { form, field, id, options, defaultLabelId, optionsDefaultLabelId, label, rawOptionLabels, useRawOptions, labelPrefix, onChanged, translate, siteGroup, isSearchable = true, errorAfterSubmit, menuPlacement, openMenuOnFocus, menuPortalTargetId } = _a, rest = tslib_1.__rest(_a, ["form", "field", "id", "options", "defaultLabelId", "optionsDefaultLabelId", "label", "rawOptionLabels", "useRawOptions", "labelPrefix", "onChanged", "translate", "siteGroup", "isSearchable", "errorAfterSubmit", "menuPlacement", "openMenuOnFocus", "menuPortalTargetId"]);
    const { values, errors, touched, submitCount } = form;
    const value = _.get(values, id);
    const error = _.get(touched, id) ? _.get(errors, id) : undefined;
    // showError will only show errors when field is touched or after submit if errorAfterSubmit is true
    const showError = errorAfterSubmit ? !!submitCount : !!_.get(touched, id);
    const { getTranslationId, getFieldTranslationId } = useLocalize();
    const getValueTranslationId = useCallback((value) => getFieldTranslationId({
        id,
        defaultLabelId: optionsDefaultLabelId || defaultLabelId,
        option: value,
    }), [id, defaultLabelId, optionsDefaultLabelId, getFieldTranslationId]);
    const getOptionValue = useCallback((option) => {
        // for searchable value, get raw translation (using non-html data to return plain string)
        const val = translate(getTranslationId({ id: getValueTranslationId(option), type: 'field' }), {
            ndash: '-',
        });
        return _.isString(val) ? val : option;
    }, [translate, getTranslationId, getValueTranslationId]);
    const getOptionLabel = useCallback((option) => {
        if (useRawOptions) {
            if (rawOptionLabels) {
                return rawOptionLabels[option] || option;
            }
            return option;
        }
        return React.createElement(Text, { id: getValueTranslationId(option), type: "field" });
    }, [useRawOptions, rawOptionLabels, getValueTranslationId]);
    const selectOptions = useMemo(() => _.map(options, o => {
        // clean the option value, since it's used for search (so we also store raw value - not displayed, used for matching original option on change)
        return {
            value: o ? (useRawOptions ? o : getOptionValue(o)) : o,
            label: o ? (getOptionLabel(o)) : (React.createElement("div", null,
                React.createElement("hr", null))),
            raw_value: o,
        };
    }), [options, getOptionValue, getOptionLabel, useRawOptions]);
    const onChange = (_e, value) => {
        if (value) {
            // Always update Formik immediately so blur-triggered validation sees the new value.
            // When onChanged is used, the parent may also call setValues - that's fine, it ensures
            // we don't validate with stale values when the dropdown blurs before the parent's update.
            form.setFieldValue(id, value);
            if (onChanged) {
                onChanged(value);
            }
            // Checks to see if the dropdown is part of a phone array, if so, format the id for the corresponding phoneNumber input and select the input
            const isPhoneArray = _.toLower(id).includes('phones');
            if (isPhoneArray) {
                const formattedId = id.substring(0, id.lastIndexOf('.')) + '.phoneNumber';
                const phoneNumberEl = document.getElementById(formattedId);
                phoneNumberEl.select();
            }
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(SelectDropdownInput, Object.assign({ name: id, label: label || (React.createElement(Text, { id: getFieldTranslationId({ id, defaultLabelId }), type: "field", data: labelPrefix && { prefix: labelPrefix }, siteGroup: siteGroup })), isSearchable: isSearchable, value: value, error: error, options: selectOptions, onblur: field.onBlur, onchange: onChange, filterOption: isSearchable ? customFilter : undefined, menuPlacement: menuPlacement, openMenuOnFocus: openMenuOnFocus, menuPortalTargetId: menuPortalTargetId }, rest)),
        error && showError && (React.createElement("span", { className: "error" },
            React.createElement(Text, { id: id, textKey: `${error}`, type: 'error', siteGroup: siteGroup })))));
};
export const SelectDropdownField = withLocalize(SelectDropdownFieldComponent);
//# sourceMappingURL=SelectDropdown.js.map